import {
  ALERT_PRIORITY_ORDER,
  LEGACY_HOSTED_IN_PAGE_ALERT_IDS,
} from 'src/alerts/AlertCenter.constants';
import { canAcknowledge } from 'src/alerts/AlertCenter.functions';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';
import type { eUserRole } from 'src/providers/UserProvider/User.enums';
import type { tAlertPanelSections } from './AlertPanel.types';

/**
 * G15 — a contextual alert (P2) lives beside the object it describes and
 * NOWHERE else. It is excluded from every bell computation — list and badge —
 * by placement, not by whether its surface happens to be mounted: judging by
 * mount state would make the badge flicker as the user navigates.
 */
function isBellEligible(alert: tLiveAlert): boolean {
  return alert.placement !== 'contextual';
}

export type tBellCountOptions = {
  /** Unacknowledged live alerts — the provider's `alerts`. */
  alerts: tLiveAlert[];
  /** Ids an inline surface has claimed it is already showing. */
  inlineAlertIds: string[];
  /** The viewer — acknowledgeability is per role (R13c). */
  role: eUserRole | null;
};

/**
 * D12 (validated 19-aug, option A) — the badge counts ONLY what the user can
 * clear: pending acknowledgeable P1s plus undismissed P3s. Everything else is
 * excluded on the same rationale, stated from the shipped racional: a badge
 * that cannot be emptied stops being read within days.
 *
 * - P0s never count (R13 makes them non-dismissible; the waiting criticals
 *   are reported by the bell's CRITICAL VISUAL STATE instead — see
 *   `AlertBell`).
 * - A P1 whose CTA resolves to nothing for this role is not clearable BY this
 *   role (R13c), so it does not count for them either.
 * - Contextual alerts are out entirely (G15).
 * - Inline-claimed alerts are already on screen elsewhere; counting them
 *   would report the same fact twice (R9).
 */
export function bellCount(options: tBellCountOptions): number {
  const { role } = options;
  if (!role) return 0;
  return options.alerts.filter(
    (alert) =>
      isBellEligible(alert) &&
      (alert.priority === 'p1' || alert.priority === 'p3') &&
      canAcknowledge(alert, role) &&
      !options.inlineAlertIds.includes(alert.id),
  ).length;
}

/**
 * U12 (KTD7) — what the bell must SIGNAL but the number can never count.
 *
 * The badge counts only what the viewer can clear (D12), and that rule is not
 * changing: a number nobody can empty stops being read within days. But the
 * inverse of that rule left a hole. `hasWaitingCritical` used to mean "a P0
 * lost the wide arbitration", so a NON-DISMISSIBLE P1 or P3 — an alert that
 * contributes zero to the badge precisely because it cannot be acknowledged —
 * lit nothing at all. The past-due ladder is exactly that shape for its first
 * nine days, which is where 76% of recoveries happen.
 *
 * Inline-claimed alerts are excluded on the R9 rationale the badge already
 * uses: the wide strip's winner and the in-page banner are permanently on
 * screen, so the bell shouting about them would report the same fact twice.
 * That exclusion is also what preserves today's behaviour for the criticals —
 * the strip registers its winner, the losers stay countable here.
 *
 * The waiting list is folded in rather than replaced: it is the provider's
 * arbitration output and is deliberately NOT inline-registered, so it would
 * otherwise be invisible to a function that reads `alerts` alone.
 *
 * 🔴 THE LEGACY-HOSTED PAIR IS EXCLUDED BY ID, because the inline-claim check
 * cannot see them. `user-inactive` and `subscription-promo` are painted by
 * their own pre-Alert-Center banners (`InactiveUserBanner` R15,
 * `SubscriptionBanner` R16) and `AlertInPage` deliberately refuses to paint
 * them — so NOTHING calls `registerInlineAlert` for either one. Both are
 * `dismissible: false`, so without this they enter the list and light the dot
 * over a banner that is already permanently on screen: the exact double report
 * the inline-claim rule exists to prevent. `subscription-promo` derives on
 * `!hasActivePlan`, so that would have been a permanent, unclearable dot for
 * every owner without a plan — noise on a permanence indicator, which is the
 * failure D12 already names for the badge ("a badge that cannot be emptied
 * stops being read within days").
 *
 * ⚠️ This is a containment, not the fix. The real fix is for those two banners
 * to call `registerInlineAlert` themselves, which would be surface-accurate by
 * construction — no dot on the three pages that mount them, a dot on the pages
 * that do not. That changes the bell reach of two families unrelated to
 * past-due, so it belongs to the Alert Center owner (queued for Ariel with the
 * `ALERT_WINDOW_RANK` placement question).
 *
 * 🔴 KTD10 — THE WIDENING IS ITSELF BEHIND THE FLAG, and that is not optional.
 * Gating the past-due DATA alone would have left this rule live at merge: the
 * rungs would stay dark (their fields are empty with the flag off) while
 * `calendar-revoked` and `la-trial-cap` — non-dismissible families with nothing
 * to do with past due — started lighting the bell for real users before anyone
 * flipped anything. That falsifies the branch's central claim, the one every
 * commit message on it makes: merging changes nothing until the flag flips.
 *
 * With `isLadderEnabled: false` this returns the waiting criticals verbatim,
 * which is byte-for-byte the input `staging` fed to its `length > 0` check —
 * every wide alert is a P0 (pinned by the window-rank exhaustiveness test), so
 * `bellSignalTone` resolves `'critical'` exactly when `staging` did. The flag
 * is taken as an INPUT rather than read from `envs` here, following
 * `resolvePastDueSnapshot`: a pure function keeps both branches trivially
 * testable, and the DoD rests on being able to prove the off branch.
 */
export type tPermanentBellOptions = {
  /** Unacknowledged live alerts — the provider's `alerts`. */
  alerts: tLiveAlert[];
  /** Losing criticals, shortest window first — the provider's ordered list. */
  waitingCriticalAlerts: tLiveAlert[];
  /** Ids an inline surface has claimed it is already showing. */
  inlineAlertIds: string[];
  /** The viewer — acknowledgeability is per role (R13c). */
  role: eUserRole | null;
  /** `VITE_PAST_DUE_LADDER_FEATURE`. Off = the pre-U12 rule, unchanged. */
  isLadderEnabled: boolean;
};

export function permanentBellAlerts(
  options: tPermanentBellOptions,
): tLiveAlert[] {
  const { role } = options;
  if (!role) return [];
  // KTD10 — the off branch is `staging`, not a narrowed version of the new
  // rule: the same list, unfiltered, that fed `waitingCriticalAlerts.length > 0`.
  if (!options.isLadderEnabled) return options.waitingCriticalAlerts;
  const claimed = new Set(options.inlineAlertIds);
  const seen = new Set<string>();
  const permanent: tLiveAlert[] = [];
  for (const alert of [...options.waitingCriticalAlerts, ...options.alerts]) {
    if (!isBellEligible(alert)) continue;
    if (LEGACY_HOSTED_IN_PAGE_ALERT_IDS.includes(alert.id)) continue;
    if (claimed.has(alert.id)) continue;
    if (canAcknowledge(alert, role)) continue;
    if (seen.has(alert.key)) continue;
    seen.add(alert.key);
    permanent.push(alert);
  }
  return permanent;
}

/**
 * The TONE the bell takes, from the highest-severity alert this role cannot
 * clear.
 *
 * `AlertBell__Badge`, `AlertBell__CriticalDot` and `AlertBell__Button--critical`
 * are hardcoded error red. Left that way the past-due ladder would turn the
 * most visible element in the app red on day 0 — the ramp broken exactly where
 * it is meant to be gentlest, and a customer whose card simply bounced meeting
 * the same colour as a banned WhatsApp account.
 *
 * ⚠️ The NUMERIC BADGE is untouched by this. Every existing family keeps
 * today's behaviour there; only the dot and the button's tint follow the ramp.
 *
 * Severity first (a live P0 always wins), then the declaration's own palette:
 * the two `prominence` members the ladder adds are palette-only, so a rung that
 * declares one is asking for that hue on every surface it reaches, the bell
 * included. A P1 with no prominence keeps its tier's warning hue; P2/P3 keep
 * info. Null when there is nothing permanent to signal.
 */
export type tBellTone = 'critical' | 'warn' | 'soft' | 'info';

export function bellSignalTone(alerts: tLiveAlert[]): tBellTone | null {
  if (alerts.length === 0) return null;
  const [loudest] = [...alerts].sort(
    (a, b) =>
      ALERT_PRIORITY_ORDER[a.priority] - ALERT_PRIORITY_ORDER[b.priority],
  );
  if (loudest.priority === 'p0') return 'critical';
  if (loudest.prominence === 'warn') return 'warn';
  if (loudest.prominence === 'soft') return 'soft';
  return loudest.priority === 'p1' ? 'warn' : 'info';
}

export type tPanelSectionsOptions = {
  /** Losing criticals, shortest window first — the provider's ordered list. */
  waitingCriticalAlerts: tLiveAlert[];
  /** Unacknowledged live alerts — the provider's `alerts`. */
  alerts: tLiveAlert[];
};

/**
 * M5 — the bell absorbs everything non-critical, in three sections:
 *
 * - "Críticos en espera": the P0s that LOST the wide arbitration. Already
 *   rank-ordered by the provider (D4 table); the winner is not here because
 *   it is permanently visible in the wide strip.
 * - "Requieren tu decisión": pending P1s.
 * - "Novedades": undismissed P3s.
 *
 * P2 never appears (G15). Sections preserve the input order: selection
 * already sorted within a tier, and the waiting list carries the window
 * ranks.
 */
export function partitionPanelSections(
  options: tPanelSectionsOptions,
): tAlertPanelSections {
  const eligible = options.alerts.filter(isBellEligible);
  return {
    waiting: options.waitingCriticalAlerts.filter(isBellEligible),
    decisions: eligible.filter((alert) => alert.priority === 'p1'),
    news: eligible.filter((alert) => alert.priority === 'p3'),
  };
}

export type tPanelAlignmentOptions = {
  /** `containerRef.current.getBoundingClientRect().left` at mount. */
  panelLeft: number;
};

/**
 * `right-0` is only a valid anchor for the panel when the bell has at least
 * the panel's width of room to its LEFT — true in the navbar's right
 * cluster, false on the Home dashboard where the bell sits next to the
 * workspace switcher on the left. Bell position is layout-dependent, so this
 * cannot be decided from CSS alone: it is measured once at mount and flips
 * the anchor when the right-aligned panel would overflow the viewport's left
 * gutter. `< 8` (rather than `< 0`) keeps a hair of breathing room instead of
 * flipping only at the exact clip point.
 */
export function resolvePanelAlignment(
  options: tPanelAlignmentOptions,
): 'right' | 'left' {
  return options.panelLeft < 8 ? 'left' : 'right';
}

export type tAcknowledgeableOptions = {
  alerts: tLiveAlert[];
  role: eUserRole | null;
};

/**
 * What "mark all as seen" is allowed to touch.
 *
 * R13c is the whole point: a P1 whose CTA resolves to nothing for this role is
 * not acknowledgeable BY this role. Without that, an admin's mark-all clears an
 * owner-only revenue alert out of their own view with nothing routing it to the
 * owner — the alert is gone and the bill still goes unpaid. Contextual alerts
 * are excluded for the same reason they are not listed (G15): the bell must
 * not act on what it does not show.
 */
export function acknowledgeableAlerts(
  options: tAcknowledgeableOptions,
): tLiveAlert[] {
  const { role } = options;
  if (!role) return [];
  return options.alerts.filter(
    (alert) => isBellEligible(alert) && canAcknowledge(alert, role),
  );
}
